import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useClientAuth } from '../context/ClientAuthContext';
import { useLanguage } from '../context/LanguageContext';
import { User, Plane, FileText, Compass, LogOut, Star } from 'lucide-react';
import ClientBottomTabBar from './ClientBottomTabBar';
import ClientOnboarding from './ClientOnboarding';
import LanguagePreferenceModal from './LanguagePreferenceModal';
import ClientLanguageSwitcher from './ClientLanguageSwitcher';
import { GOLD, NAVY, CREAM, alpha } from '../theme/palette';

// Belmond Cathedral tokens

const t = (lang, pt, en, es) => lang === 'pt' ? pt : lang === 'en' ? en : es;

const ClientNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, isAuthenticated } = useClientAuth();
  const { language } = useLanguage();

  if (!isAuthenticated) return null;

  const handleLogout = () => { logout(); navigate('/'); };

  const leftItems = [
    { path: '/minhas-viagens', label: t(language, 'Minhas Viagens', 'My Trips', 'Mis Viajes'), icon: Plane },
    { path: '/my-documents', label: t(language, 'Meus Documentos', 'My Documents', 'Mis Documentos'), icon: FileText },
    { path: '/depoimentos', label: t(language, 'Depoimentos', 'Testimonials', 'Testimonios'), icon: Star },
    { path: '/recursos', label: t(language, 'Recursos', 'Resources', 'Recursos'), icon: Compass },
  ];

  const isActive = (path) => {
    if (path === '/minhas-viagens') return location.pathname === '/minhas-viagens' || location.pathname.startsWith('/my-bookings');
    if (path === '/my-documents') return location.pathname === '/my-documents' || location.pathname.startsWith('/my-documents');
    if (path === '/depoimentos') return location.pathname === '/depoimentos' || location.pathname === '/testimonials';
    if (path === '/recursos') return location.pathname.startsWith('/recursos');
    return location.pathname === path;
  };

  const TabButton = ({ item, active, testId, compact = false }) => {
    const Icon = item.icon;
    return (
      <button
        onClick={() => navigate(item.path)}
        className={`inline-flex items-center justify-center gap-1.5 ${compact ? 'px-2 sm:px-2.5 lg:px-3.5' : 'px-3 sm:px-3.5 lg:px-4'} py-1.5 sm:py-2 text-[10px] sm:text-[10px] lg:text-[11px] tracking-[0.15em] sm:tracking-[0.18em] lg:tracking-[0.22em] uppercase font-medium whitespace-nowrap transition`}
        style={{
          background: active ? NAVY : 'transparent',
          color: active ? GOLD : NAVY,
          border: `1px solid ${active ? NAVY : alpha(GOLD, '55')}`,
          borderRadius: '9999px',
        }}
        data-testid={testId}
      >
        <Icon className="w-3 h-3 sm:w-3.5 sm:h-3.5 flex-shrink-0" />
        <span className="truncate">{item.label}</span>
      </button>
    );
  };

  return (
    <>
      <ClientOnboarding />
      <LanguagePreferenceModal />
      <ClientBottomTabBar />
      <div
        className="fixed top-[132px] lg:top-[144px] left-0 right-0 z-40 border-b backdrop-blur hidden sm:block"
        style={{ background: `${alpha(CREAM, 'f0')}`, borderColor: `${alpha(GOLD, '44')}` }}
        data-testid="client-nav"
      >
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6">
          {/* Mobile: 2 rows explicitly grouped (nav grid + user controls row).
              Desktop (sm+): single flex row like before. */}
          <div className="py-2 sm:py-3 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-1.5">
            <span className="text-[10px] tracking-[0.35em] uppercase mr-2 opacity-50 hidden xl:inline" style={{ color: NAVY }}>
              {t(language, 'Minha Conta:', 'My Account:', 'Mi Cuenta:')}
            </span>
            {/* Primary nav — 2×2 grid on mobile, inline flex on desktop */}
            <div className="grid grid-cols-2 gap-1.5 sm:flex sm:flex-wrap sm:gap-1.5">
              {leftItems.map((item) => (
                <TabButton key={item.path} item={item} active={isActive(item.path)} testId={`client-nav-${item.path.replace('/', '')}`} compact />
              ))}
            </div>
            {/* User controls — right-aligned on both breakpoints */}
            <div className="flex items-center justify-end gap-1.5 sm:ml-auto">
              <ClientLanguageSwitcher />
              <TabButton
                item={{ path: '/meu-perfil', label: t(language, 'Meu Perfil', 'My Profile', 'Mi Perfil'), icon: User }}
                active={location.pathname === '/meu-perfil'}
                testId="client-nav-meu-perfil"
                compact
              />
              <button
                onClick={handleLogout}
                className="inline-flex items-center gap-1.5 px-2 sm:px-2.5 lg:px-3.5 py-1.5 sm:py-2 text-[10px] lg:text-[11px] tracking-[0.15em] sm:tracking-[0.18em] lg:tracking-[0.22em] uppercase font-medium whitespace-nowrap transition hover:bg-red-50"
                style={{ color: '#c0392b', border: '1px solid rgba(192,57,43,0.3)', borderRadius: '9999px' }}
                data-testid="client-nav-logout"
              >
                <LogOut className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                {t(language, 'Sair', 'Log out', 'Cerrar sesión')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ClientNav;
